import React, { useState } from "react";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Lock, Mail } from "lucide-react";
import ispaiLogoHorizontal from "@/assets/ispai-logo-horizontal.png";
import { verifySessionRemote, saveSessionToken, clearSession } from "@/utils/sessionStorage";

interface CampaignUnlockFormProps {
  campaignCode: string;
  onUnlock: (session: CampaignSession) => void;
}

export interface CampaignSession {
  email: string;
  campaign_code: string;
  campaign_name: string;
  unlocked_at: string;
  expires_at: string;
  is_admin?: boolean;
  remember_me?: boolean;
}

const STORAGE_KEY = "campaign_session";

export const getCampaignSession = async (): Promise<CampaignSession | null> => {
  return await verifySessionRemote<CampaignSession>(STORAGE_KEY);
};

export const saveCampaignSession = (token: string, rememberMe: boolean = false): void => {
  saveSessionToken(STORAGE_KEY, token, rememberMe);
};

export const clearCampaignSession = () => {
  clearSession(STORAGE_KEY);
};

const CampaignUnlockForm: React.FC<CampaignUnlockFormProps> = ({ campaignCode, onUnlock }) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);

  const handleLogin = async () => {
    // First, try admin authentication
    const { data: authData, error: authError } = await supabase.functions.invoke('verify-campaign-password', {
      body: {
        email: email.trim(),
        campaign_code: '',
        password,
        remember_me: rememberMe,
      }
    });

    if (!authError && authData?.success && authData?.session_token) {
      saveCampaignSession(authData.session_token, rememberMe);
      const session = await verifySessionRemote<CampaignSession>(STORAGE_KEY);
      if (session) {
        toast({
          title: authData.is_admin ? "Admin Access Granted" : "Access Granted",
          description: authData.is_admin
            ? "Welcome! You have admin access to the investment terms."
            : "Welcome! You now have access to the investment terms."
        });
        onUnlock(session);
      }
      return;
    }

    // Try campaign password
    const { data, error } = await supabase.functions.invoke('verify-campaign-password', {
      body: {
        email: email.trim(),
        campaign_code: campaignCode,
        password,
        remember_me: rememberMe,
      }
    });

    if (error || !data?.success || !data?.session_token) {
      toast({
        title: "Access Denied",
        description: data?.error || "Incorrect email or password. Please try again.",
        variant: "destructive"
      });
      return;
    }

    saveCampaignSession(data.session_token, rememberMe);
    const session = await verifySessionRemote<CampaignSession>(STORAGE_KEY);
    if (session) {
      toast({
        title: "Access Granted",
        description: `Welcome! You now have access to the ${session.campaign_name} investment terms.`
      });
      onUnlock(session);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await handleLogin();
    } catch (error) {
      console.error("Form error:", error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="border-2 border-black rounded-xl overflow-hidden">

      <div className="bg-gradient-to-br from-gray-50 to-blue-50 p-8">
        <div className="max-w-md mx-auto text-center">
          <div className="mb-6">
            <div className="w-32 h-32 flex items-center justify-center mx-auto mb-4">
              <img src={ispaiLogoHorizontal} alt="iSPAI" className="w-32 h-32 object-contain" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Exclusive Investment Terms
            </h2>
            <p className="text-gray-600">
              Enter your credentials to view the iSPAI investment terms and submit your investment request.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="text-left">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 w-full"
                  placeholder="your@email.com"
                  autoComplete="off" />
              </div>
            </div>

            <div className="text-left">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Access Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 w-full"
                  placeholder="Enter access password"
                  autoComplete="off" />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="rememberMe"
                checked={rememberMe}
                onCheckedChange={(checked) => setRememberMe(checked === true)} />
              <label
                htmlFor="rememberMe"
                className="text-sm font-medium text-gray-700 cursor-pointer">
                Remember me for 10 days
              </label>
            </div>

            {rememberMe && (
              <p className="text-xs text-gray-500">
                Your session will persist on this device
              </p>
            )}

            <Button
              type="submit"
              size="lg"
              disabled={isSubmitting}
              className="w-full bg-black hover:bg-gray-800 text-white font-semibold py-3">
              {isSubmitting ? "Please wait..." : "View Investment Terms"}
            </Button>
          </form>

          <p className="mt-4 text-sm text-gray-500">
            Need help? Contact{" "}
            <a href="mailto:invest@ispai.io" className="text-blue-600 hover:text-blue-800 underline">
              invest@ispai.io
            </a>
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default CampaignUnlockForm;
